import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Radio, ShieldAlert, CheckCircle2, XCircle, Ban, RefreshCw, Store, Clock, Receipt, Coins, Users, CalendarDays, Printer, Download, MessageSquare } from "lucide-react";
import QueriesBoard from "../components/QueriesBoard";
import { downloadReport } from "./accounting/exportHelpers";
import FloorBoard from "./hr/FloorBoard";
import { useAuth } from "../context/AuthContext";

const N = (n) => "₦" + Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const time = (s) => s ? new Date(s).toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" }) : "—";

export default function ManagerConsole() {
  const { user } = useAuth();
  const [tab, setTab] = useState("tills");
  const [live, setLive] = useState({ tills: [], open_count: 0, total_gross: 0, total_orders: 0 });
  const [approvals, setApprovals] = useState([]);
  const [orders, setOrders] = useState([]);
  const [voidTarget, setVoidTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadLive = useCallback(() => api.get("/manager/live-tills").then(setLive).catch(() => {}), []);
  const loadApprovals = useCallback(() => api.get("/manager/approvals?status=pending").then(setApprovals).catch(() => {}), []);
  const loadOrders = useCallback(() => api.get("/manager/orders").then(setOrders).catch(() => {}), []);

  useEffect(() => { loadLive(); loadApprovals(); loadOrders(); }, [loadLive, loadApprovals, loadOrders]);
  useEffect(() => {
    const iv = setInterval(() => { loadLive(); loadApprovals(); if (tab === "voids") loadOrders(); }, 8000);
    return () => clearInterval(iv);
  }, [loadLive, loadApprovals, loadOrders, tab]);

  const decide = async (id, decision, note = "") => {
    try {
      await api.post(`/manager/approvals/${id}/decision`, { decision, note });
      toast.success(`Request ${decision}`);
      loadApprovals();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doVoid = async (reason) => {
    try {
      await api.post(`/orders/${voidTarget.id}/void`, { reason });
      toast.success(`${voidTarget.number} voided`);
      setVoidTarget(null); loadOrders(); loadLive();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const TABS = [
    ["tills", "Live Tills", Radio],
    ["floor", "Floor Board", Users],
    ["roster", "Weekly Roster", CalendarDays],
    ["queries", "Queries", MessageSquare],
    ["approvals", "Approvals", ShieldAlert, approvals.length],
    ["voids", "Void Authorisation", Ban],
  ];

  return (
    <div data-testid="manager-console" className="max-w-7xl mx-auto">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-brand text-3xl font-bold" style={{ color: "#3B2722" }}>Manager Console</h1>
          <p className="mt-1 text-sm" style={{ color: "#765A4A" }}>Live floor oversight, approvals and void authorisation</p>
        </div>
        <button onClick={async () => { setRefreshing(true); try { await Promise.all([loadLive(), loadApprovals(), loadOrders()]); toast.success("Console refreshed"); } finally { setRefreshing(false); } }}
          data-testid="mc-refresh" disabled={refreshing}
          className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg border transition-colors hover:bg-white disabled:opacity-60"
          style={{ borderColor: "#E5DCC9", color: "#6B1717", background: "rgba(255,255,255,.6)" }}>
          <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map(([k, label, Icon, badge]) => {
          const on = tab === k;
          return (
            <button key={k} onClick={() => setTab(k)} data-testid={`mc-tab-${k}`}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors border"
              style={on ? { background: "#8B1E1E", color: "#fff", borderColor: "#8B1E1E" } : { background: "#fff", color: "#3B2722", borderColor: "#E5DCC9" }}>
              <Icon size={16} style={{ color: on ? "#EBC985" : "#B8860B" }} /> {label}
              {badge ? <span className="ml-1 text-[11px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: on ? "#EBC985" : "#8B1E1E", color: on ? "#6B1717" : "#fff" }}>{badge}</span> : null}
            </button>
          );
        })}
      </div>

      {tab === "tills" && <LiveTills live={live} />}
      {tab === "floor" && (
        <div data-testid="mc-floor-board">
          <FloorBoard locationId={user?.location_id || null} title="Who's on shift now" />
        </div>
      )}
      {tab === "roster" && <Roster locationId={user?.location_id || null} isAdmin={!!user?.is_admin} />}
      {tab === "queries" && <QueriesBoard />}
      {tab === "approvals" && <Approvals approvals={approvals} onDecide={decide} onReject={(a) => setRejectTarget(a)} />}
      {tab === "voids" && <Voids orders={orders} onVoid={(o) => setVoidTarget(o)} />}

      {voidTarget && <ReasonModal title={`Void ${voidTarget.number}`} subtitle={`${voidTarget.customer_name} · ${N(voidTarget.total)}`}
        confirmLabel="Void order" testid="mc-void-modal" onClose={() => setVoidTarget(null)} onConfirm={doVoid} />}
      {rejectTarget && <ReasonModal title="Reject request" subtitle={`${rejectTarget.requested_by_name} · ${rejectTarget.type}`}
        confirmLabel="Reject" optional testid="mc-reject-modal" onClose={() => setRejectTarget(null)}
        onConfirm={(note) => { decide(rejectTarget.id, "rejected", note); setRejectTarget(null); }} />}
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl p-5 border" style={{ background: "#fff", borderColor: "#EADFCB" }}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider" style={{ color: "#765A4A" }}>
        <Icon size={15} style={{ color: "#B8860B" }} /> {label}
      </div>
      <div className="font-brand font-bold mt-2" style={{ color: "#8B1E1E", fontSize: 30 }}>{value}</div>
    </div>
  );
}

function LiveTills({ live }) {
  return (
    <div data-testid="mc-live-tills">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard label="Open Tills" value={live.open_count} icon={Radio} />
        <StatCard label="Live Gross Sales" value={N(live.total_gross)} icon={Coins} />
        <StatCard label="Orders Today" value={live.total_orders} icon={Receipt} />
      </div>
      {live.tills.length === 0 ? (
        <div className="rounded-2xl p-10 text-center border" style={{ background: "#fff", borderColor: "#EADFCB", color: "#765A4A" }}>
          No tills are currently open.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {live.tills.map((t) => (
            <div key={t.id} data-testid="mc-till-card" className="rounded-2xl p-5 border" style={{ background: "#fff", borderColor: "#EADFCB" }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#4F7A52" }} />
                  <span className="font-brand font-bold text-lg" style={{ color: "#3B2722" }}>{t.till}</span>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: "#765A4A" }}>
                  <Store size={13} style={{ color: "#B8860B" }} /> {t.location_name}
                </span>
              </div>
              <div className="text-sm mt-1" style={{ color: "#765A4A" }}>{t.cashier_name}</div>
              <div className="font-brand font-bold my-3" style={{ color: "#8B1E1E", fontSize: 26 }}>{N(t.gross_sales)}</div>
              <div className="flex items-center justify-between text-xs mb-3" style={{ color: "#765A4A" }}>
                <span>{t.orders_count} orders</span>
                <span className="inline-flex items-center gap-1"><Clock size={12} /> {time(t.last_activity)}</span>
              </div>
              <div className="grid grid-cols-4 gap-1.5 pt-3 border-t" style={{ borderColor: "#EFE6D5" }}>
                {["cash", "card", "transfer", "loyalty"].map((k) => (
                  <div key={k} className="text-center">
                    <div className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: "#A08a7a" }}>{k}</div>
                    <div className="text-[11px] font-bold mt-0.5" style={{ color: "#3B2722" }}>{N(t.tenders?.[k] || 0)}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Roster({ locationId, isAdmin }) {
  const monday = () => {
    const d = new Date();
    const diff = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - diff);
    return d.toISOString().slice(0, 10);
  };
  const [start, setStart] = useState(monday());
  const [loc, setLoc] = useState(locationId || "");
  const [locations, setLocations] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const end = (() => { const d = new Date(start); d.setDate(d.getDate() + 6); return d.toISOString().slice(0, 10); })();
  const params = { date_from: start, date_to: end, location_id: loc || undefined };

  useEffect(() => { if (isAdmin) api.get("/locations").then(setLocations).catch(() => {}); }, [isAdmin]);
  useEffect(() => {
    setLoading(true);
    api.get("/hr/shifts", { params }).then((r) => setRows(r || [])).catch(() => setRows([])).finally(() => setLoading(false));
    /* eslint-disable-next-line */
  }, [start, loc]);

  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d.toISOString().slice(0, 10); });
  const byDay = {};
  rows.forEach((r) => { (byDay[r.date] = byDay[r.date] || []).push(r); });

  return (
    <div data-testid="mc-roster">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: "#765A4A" }}>Week starting</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="roster-week"
            className="h-10 px-3 rounded-lg text-sm border" style={{ borderColor: "#E5DCC9", background: "#fff", color: "#3B2722" }} />
        </div>
        {isAdmin && (
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: "#765A4A" }}>Branch</label>
            <select value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="roster-branch"
              className="h-10 px-3 rounded-lg text-sm border" style={{ borderColor: "#E5DCC9", background: "#fff", color: "#3B2722" }}>
              <option value="">All branches</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        )}
        <div className="flex-1" />
        <button onClick={() => downloadReport("/hr/exports/roster.csv", `roster-${start}.csv`, params)} data-testid="roster-csv"
          className="inline-flex items-center gap-2 h-10 px-3.5 rounded-lg text-sm font-semibold border" style={{ borderColor: "#E5DCC9", background: "#fff", color: "#3B2722" }}>
          <Download size={15} /> CSV
        </button>
        <button onClick={() => downloadReport("/hr/exports/roster.pdf", `roster-${start}.pdf`, params)} data-testid="roster-print"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg text-sm font-bold text-white" style={{ background: "#8B1E1E" }}>
          <Printer size={15} /> Print Roster
        </button>
      </div>

      {loading ? <div className="h-40 rounded-2xl animate-pulse" style={{ background: "#F3EAD6" }} />
        : rows.length === 0 ? (
          <div className="rounded-2xl p-10 text-center border" style={{ background: "#fff", borderColor: "#EADFCB", color: "#765A4A" }}>
            No shifts scheduled for {start} → {end}.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {days.map((d) => (
              <div key={d} className="rounded-2xl border p-3" style={{ background: "#fff", borderColor: "#EADFCB" }} data-testid={`roster-day-${d}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold" style={{ color: "#3B2722" }}>
                    {new Date(d).toLocaleDateString("en-NG", { weekday: "short", day: "numeric", month: "short" })}
                  </span>
                  <span className="text-xs" style={{ color: "#A08a7a" }}>{(byDay[d] || []).length}</span>
                </div>
                <div className="space-y-1.5">
                  {(byDay[d] || []).map((r) => (
                    <div key={r.id} className="rounded-lg px-2 py-1.5 border" style={{ borderColor: "#F0E9DD", background: "#FFFCF7" }}>
                      <div className="text-[11.5px] font-semibold truncate" style={{ color: "#3B2722" }}>{r.employee_name}</div>
                      <div className="text-[10px]" style={{ color: "#765A4A" }}>{r.shift_name} · {r.start_time}–{r.end_time}</div>
                    </div>
                  ))}
                  {!(byDay[d] || []).length && <div className="text-[11px] py-3 text-center" style={{ color: "#A08a7a" }}>No shifts</div>}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function Approvals({ approvals, onDecide, onReject }) {
  return (
    <div data-testid="mc-approvals">
      {approvals.length === 0 ? (
        <div className="rounded-2xl p-10 text-center border" style={{ background: "#fff", borderColor: "#EADFCB", color: "#765A4A" }}>
          No pending approval requests.
        </div>
      ) : (
        <div className="space-y-3">
          {approvals.map((a) => (
            <div key={a.id} data-testid="mc-approval-row" className="rounded-2xl p-4 border flex items-center justify-between flex-wrap gap-3" style={{ background: "#fff", borderColor: "#EADFCB" }}>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: "#FBEEDA", color: "#8B6914" }}>{a.type}</span>
                  <span className="font-semibold" style={{ color: "#3B2722" }}>{a.requested_by_name}</span>
                  <span className="text-xs" style={{ color: "#765A4A" }}>· {a.till} · {a.location_name}</span>
                </div>
                <div className="text-sm mt-1" style={{ color: "#765A4A" }}>{a.reason || "—"}{a.amount ? ` · ${N(a.amount)}` : ""} · {time(a.created_at)}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => onDecide(a.id, "approved")} data-testid={`mc-approve-${a.id}`}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: "#4F7A52" }}>
                  <CheckCircle2 size={15} /> Approve
                </button>
                <button onClick={() => onReject(a)} data-testid={`mc-reject-${a.id}`}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold" style={{ background: "#fff", color: "#A52222", border: "1px solid #eecccc" }}>
                  <XCircle size={15} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Voids({ orders, onVoid }) {
  return (
    <div data-testid="mc-voids" className="space-y-2">
      {orders.length === 0 ? (
        <div className="rounded-2xl p-10 text-center border" style={{ background: "#fff", borderColor: "#EADFCB", color: "#765A4A" }}>No POS orders yet.</div>
      ) : orders.map((o) => {
        const voided = o.fulfilment_status === "voided";
        return (
          <div key={o.id} data-testid="mc-order-row" className="rounded-xl p-4 border flex items-center justify-between flex-wrap gap-3" style={{ background: "#fff", borderColor: "#EADFCB", opacity: voided ? 0.7 : 1 }}>
            <div className="flex items-center gap-4 flex-wrap">
              <span className="font-bold" style={{ color: "#8B1E1E" }}>{o.number}</span>
              <span className="text-sm" style={{ color: "#3B2722" }}>{o.customer_name}</span>
              <span className="text-xs capitalize" style={{ color: "#765A4A" }}>{(o.order_type || "").replace("_", " ")} · {o.location_name} · {time(o.created_at)}</span>
              <span className="font-bold" style={{ color: "#3B2722" }}>{N(o.total)}</span>
            </div>
            {voided ? (
              <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: "#F7E0E0", color: "#A52222" }}>VOIDED · {o.voided_by}</span>
            ) : (
              <button onClick={() => onVoid(o)} data-testid={`mc-void-${o.id}`}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold" style={{ background: "#fff", color: "#A52222", border: "1px solid #eecccc" }}>
                <Ban size={15} /> Void
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ReasonModal({ title, subtitle, confirmLabel, optional, testid, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => { setBusy(true); await onConfirm(reason.trim()); setBusy(false); };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(59,39,34,.45)" }} data-testid={testid}>
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: "#fff" }}>
        <h3 className="font-brand text-xl font-bold" style={{ color: "#8B1E1E" }}>{title}</h3>
        <p className="text-sm mt-1 mb-4" style={{ color: "#765A4A" }}>{subtitle}</p>
        <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#765A4A" }}>Reason{optional ? " (optional)" : ""}</label>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} autoFocus data-testid="mc-reason-input"
          placeholder="Enter reason…" className="w-full mt-2 rounded-lg p-3 text-sm outline-none" style={{ background: "#FFFCF7", border: "1px solid #E9DED0", color: "#3B2722" }} />
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 py-3 rounded-lg text-sm font-semibold" style={{ background: "#fff", color: "#765A4A", border: "1px solid #E9DED0" }}>Cancel</button>
          <button onClick={submit} disabled={busy || (!optional && !reason.trim())} data-testid="mc-reason-confirm"
            className="flex-1 py-3 rounded-lg text-sm font-bold text-white disabled:opacity-50" style={{ background: "#8B1E1E" }}>{busy ? "Working…" : confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
